// Destinations content catalog — reusable structured model (Step 19).
// Placeholder editorial content; swap for real Holideviser copy later.

const img = (id, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=${w}&auto=format`;

export const DEST_REGIONS = [
  "Europe",
  "Asia",
  "Middle East",
  "Africa",
  "North America",
  "Latin America & Caribbean",
  "Oceania",
];

export const INTENTIONS = [
  { id: "food-obsessed", label: "For the Food-Obsessed" },
  { id: "slow-escapes", label: "For Slow Escapes" },
  { id: "beach-late-nights", label: "For Beach Days & Late Nights" },
  { id: "romantic", label: "For Romantic Getaways" },
  { id: "design-lovers", label: "For Design Lovers" },
  { id: "adventure", label: "For Adventure Seekers" },
  { id: "music-nightlife", label: "For Music & Nightlife" },
  { id: "luxury", label: "For a Little Luxury" },
  { id: "first-timers", label: "For First-Time Explorers" },
];

// REAL, founder-aligned destinations shown publicly in the Destinations directory.
// Each links to its existing dedicated edit page (/destinations/<slug>).
export const REAL_DESTINATIONS = [
  {
    slug: "da-nang",
    name: "Da Nang",
    country: "Vietnam",
    region: "Asia",
    image:
      "https://images.unsplash.com/photo-1558002890-c0b30998d1e6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&auto=format",
    tagline: "Vietnam's quiet coast, at the pace it deserves.",
    shortDescription: "Vietnam's quiet coast — an edit we're building slowly, only from places we've stood in.",
    tags: ["Coast", "Food", "Nightlife"],
    bestFor: ["Couples", "Slow travel"],
    intentions: ["slow-escapes", "food-obsessed", "romantic", "music-nightlife"],
  },
  {
    slug: "ho-chi-minh-city",
    name: "Ho Chi Minh City",
    country: "Vietnam",
    region: "Asia",
    image:
      "https://images.unsplash.com/photo-1578768124932-e0cc82f7a37e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&auto=format",
    tagline: "The country's loudest, fastest, most delicious city.",
    shortDescription: "The country's loudest, fastest city — starting with places we've experienced first-hand.",
    tags: ["Food", "City", "Culture"],
    bestFor: ["Café hopping", "First-timers"],
    intentions: ["food-obsessed", "music-nightlife", "first-timers"],
  },
  {
    slug: "goa",
    name: "Goa",
    country: "India",
    region: "Asia",
    image:
      "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&auto=format",
    tagline: "Local knowledge, built from the inside.",
    shortDescription: "Local knowledge, built from the inside — starting with places we know firsthand.",
    tags: ["Nightlife", "Beach", "Food"],
    bestFor: ["Couples", "Date nights"],
    intentions: ["romantic", "beach-late-nights", "music-nightlife"],
  },
];

export const DESTINATIONS = [
  {
    slug: "lisbon",
    name: "Lisbon",
    country: "Portugal",
    region: "Europe",
    image: img("1783465942278-7f4fd40259bf"),
    tagline: "Easygoing mornings. Serious food. Nights that rarely end when planned.",
    shortDescription:
      "Come for the food. Stay because the city makes wandering feel like a plan.",
    tags: ["Food", "Culture", "Nightlife"],
    bestFor: ["Couples", "Friends", "First-time Europe"],
    intentions: ["food-obsessed", "romantic", "music-nightlife", "first-timers"],
    pov: "Lisbon is best experienced without trying to conquer it. Pick a neighbourhood, walk until you're hungry, eat late, and let the evening decide what happens next. The hills, the light and the trams are the postcard — but the city lives in its tascas, miradouros and the long, unhurried lunch.",
    whyGo: [
      { title: "Food", note: "One of Europe's most underrated eating cities." },
      { title: "Neighbourhoods", note: "Alfama, Príncipe Real, Cais do Sodré — each its own mood." },
      { title: "Light", note: "The famous Atlantic light that softens everything by evening." },
      { title: "Value", note: "Still gentler on the wallet than most of Western Europe." },
    ],
    whoFor: [
      "You'd rather discover a neighbourhood than tick off landmarks.",
      "You plan your trips around dinner.",
      "You want nightlife, but not every single night.",
      "You like a city that rewards wandering on foot.",
    ],
    whenToGo: {
      bestMonths: "April–June, September–October",
      shoulder: "March, November",
      peak: "July–August",
      weather: "Warm, dry summers; mild, walkable spring and autumn.",
      events: ["Festas de Lisboa (June)", "Summer rooftop season"],
    },
    tripLengths: [
      { days: "3 Days", note: "A taste of the city and its best tables." },
      { days: "5 Days", note: "Enough to settle into a neighbourhood." },
      { days: "7 Days", note: "Go deeper — add a day trip to Sintra or the coast." },
    ],
    experiences: {
      do: ["Ride tram 28 early, before the crowds", "A sunset at a quiet miradouro"],
      see: ["Alfama's tangle of lanes", "The tile façades of Príncipe Real"],
      eat: ["A long seafood lunch in Cais do Sodré", "Pastéis where the queue actually moves"],
      drink: ["Ginjinha from a hole-in-the-wall", "Natural wine in Príncipe Real"],
      stay: ["A restored townhouse over a chain hotel"],
      wander: ["Downhill from Graça to the river with no plan"],
    },
    skip: [
      "The Pink Street at peak hour — it's better late and quieter.",
      "Restaurants with a host waving a menu at you.",
      "Trying to 'do' Sintra and Cascais in the same rushed day.",
    ],
    whereToStay: [
      {
        audience: "For Design Lovers",
        hotel: "A boutique townhouse stay",
        neighborhood: "Príncipe Real",
        why: "Leafy, low-key and full of independent shops and wine bars.",
        price: "Mid–High",
      },
      {
        audience: "For Romantics",
        hotel: "A small hotel with a view",
        neighborhood: "Alfama",
        why: "Waking up over the rooftops with fado drifting up the hill.",
        price: "Mid",
      },
      {
        audience: "For Night Owls",
        hotel: "A lively address near the bars",
        neighborhood: "Cais do Sodré",
        why: "Steps from dinner, drinks and the river.",
        price: "Mid",
      },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Fresh grilled fish", "A proper bifana"] },
      { category: "Local Classics", items: ["Bacalhau, done well", "Pastel de nata, warm"] },
      { category: "Special Occasion", items: ["A tasting menu with a river view"] },
      { category: "Coffee & Cocktails", items: ["A bica standing at the counter", "Rooftop negroni at dusk"] },
    ],
    suggestedTripStyles: [
      { title: "Lisbon for Food Lovers", note: "Structured around markets, tascas and long lunches." },
      { title: "Lisbon for a Long Weekend", note: "Three neighbourhoods, no rushing." },
      { title: "Lisbon After Dark", note: "Late dinners, fado you stumble into, and Cais do Sodré." },
      { title: "Lisbon + Porto", note: "Pair the capital with its quieter northern sibling." },
    ],
    relatedSlugs: ["barcelona", "mexico-city", "marrakech"],
    seo: {
      title: "Lisbon Travel Guide — Where to Go, Eat & Stay | Holideviser",
      description:
        "A curated Holideviser point of view on Lisbon: what to eat, where to stay, what to skip and how long to go. Travel Lisbon like a local with taste.",
    },
  },

  {
    slug: "barcelona",
    name: "Barcelona",
    country: "Spain",
    region: "Europe",
    image: img("1579282240050-352db0a14c21"),
    tagline: "Architecture, appetite and the sea — a city that refuses to be dull.",
    shortDescription: "Gaudí gets the headlines, but the tapas bars and back streets steal the trip.",
    tags: ["Design", "Food", "Beach"],
    bestFor: ["Couples", "Friends", "Design lovers"],
    intentions: ["food-obsessed", "design-lovers", "music-nightlife", "first-timers"],
    pov: "Barcelona rewards the traveller who books the restaurant before the museum. See a little Gaudí, yes — but spend the real hours in Gràcia and the Gothic Quarter, eating standing up and finishing days by the water.",
    whyGo: [
      { title: "Architecture", note: "Gaudí and modernisme at every turn." },
      { title: "Food", note: "Tapas, seafood and Catalan markets." },
      { title: "Beach", note: "A city that ends at the Mediterranean." },
      { title: "Barrios", note: "Gràcia, El Born, Gothic Quarter — distinct worlds." },
    ],
    whoFor: [
      "You want a beach and a serious dinner in the same day.",
      "You'd rather graze at bars than sit for three-hour meals.",
      "You like design that spills onto the street.",
      "You want energy without needing a plan.",
    ],
    whenToGo: {
      bestMonths: "May–June, September",
      shoulder: "April, October",
      peak: "July–August",
      weather: "Hot summers, mild and pleasant shoulder seasons.",
      events: ["La Mercè (September)", "Summer beach season"],
    },
    tripLengths: [
      { days: "3 Days", note: "Highlights, tapas and one beach afternoon." },
      { days: "5 Days", note: "Add Gràcia, day-trip wine country." },
      { days: "7 Days", note: "Slow it down; live like a local." },
    ],
    experiences: {
      do: ["A tapas crawl through El Born", "A morning at a Gaudí site, early"],
      see: ["Sagrada Família (booked ahead)", "The rooftops of modernisme"],
      eat: ["Market pintxos", "Seafood by the Barceloneta"],
      drink: ["Vermut before lunch", "A back-street cocktail bar"],
      stay: ["A design hotel in El Born"],
      wander: ["Gràcia's squares in the early evening"],
    },
    skip: [
      "Las Ramblas restaurants — tourist traps with a view.",
      "Queuing on-site for Gaudí; book everything ahead.",
      "The beach at peak August if you value elbow room.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A modernisme boutique", neighborhood: "Eixample", why: "Surrounded by the architecture you came for.", price: "High" },
      { audience: "For Romantics", hotel: "A quiet Gothic hideaway", neighborhood: "El Born", why: "Medieval lanes and candlelit dinners.", price: "Mid–High" },
      { audience: "For Families", hotel: "An apartment near the beach", neighborhood: "Barceloneta", why: "Space, sand and easy mornings.", price: "Mid" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Pan con tomate", "Fresh anchovies"] },
      { category: "Local Classics", items: ["Fideuà", "Crema catalana"] },
      { category: "Special Occasion", items: ["A modern Catalan tasting menu"] },
      { category: "Coffee & Cocktails", items: ["Cortado mid-morning", "Vermut o'clock"] },
    ],
    suggestedTripStyles: [
      { title: "Barcelona for Design Lovers", note: "Modernisme, galleries and concept stores." },
      { title: "Barcelona for a Long Weekend", note: "City, sea and tapas in three days." },
      { title: "Barcelona After Dark", note: "Late dinners and back-street bars." },
    ],
    relatedSlugs: ["lisbon", "tokyo", "new-york"],
    seo: {
      title: "Barcelona Travel Guide — Design, Food & Beach | Holideviser",
      description:
        "Holideviser's opinionated Barcelona guide: what to eat, where to stay, what to skip and how to balance Gaudí with the city's real life.",
    },
  },

  {
    slug: "kyoto",
    name: "Kyoto",
    country: "Japan",
    region: "Asia",
    image: img("1624253321171-1be53e12f5f4"),
    tagline: "Go slow, go early, and skip the temple everyone photographs.",
    shortDescription: "Old Japan at a whisper — if you time it right and wake up before the buses.",
    tags: ["Culture", "Design", "Slow"],
    bestFor: ["Couples", "Solo", "Slow travellers"],
    intentions: ["slow-escapes", "design-lovers", "romantic"],
    pov: "Kyoto rewards patience and early alarms. See one great garden before the tour buses, drink tea somewhere unmarked, and let the back lanes of Gion do the work. The city is quietest — and best — at its edges.",
    whyGo: [
      { title: "Temples & Gardens", note: "Centuries of design, best at opening." },
      { title: "Craft", note: "Tea, ceramics, textiles and quiet mastery." },
      { title: "Food", note: "Kaiseki, tofu, and immaculate markets." },
      { title: "Seasons", note: "Autumn colour and spring blossom are worth planning around." },
    ],
    whoFor: [
      "You'd trade a checklist for one perfect morning.",
      "You appreciate craft and quiet.",
      "You'll set an early alarm for an empty garden.",
      "You like beauty in restraint.",
    ],
    whenToGo: {
      bestMonths: "October–November, March–April",
      shoulder: "May, September",
      peak: "Cherry-blossom & autumn-colour weeks",
      weather: "Cold winters, humid summers; glorious spring and autumn.",
      events: ["Autumn foliage season", "Cherry blossom (variable)"],
    },
    tripLengths: [
      { days: "3 Days", note: "The essentials, done calmly." },
      { days: "5 Days", note: "Add craft, tea and a day in the hills." },
      { days: "7 Days", note: "Pair with Osaka or Nara." },
    ],
    experiences: {
      do: ["A garden at opening", "A tea experience away from the crowds"],
      see: ["Gion's back lanes at dusk", "A temple most people skip"],
      eat: ["Nishiki Market, early", "A quiet kaiseki dinner"],
      drink: ["Matcha, properly made", "Sake at a tiny counter"],
      stay: ["A machiya townhouse or a ryokan"],
      wander: ["The Philosopher's Path before 8am"],
    },
    skip: [
      "Fushimi Inari mid-morning — go at dawn or not at all.",
      "Renting a kimono to fight for the same photo as everyone else.",
      "Cramming five temples into one exhausting day.",
    ],
    whereToStay: [
      { audience: "For Quiet Seekers", hotel: "A traditional ryokan", neighborhood: "Higashiyama", why: "Tatami, silence and a garden view.", price: "High" },
      { audience: "For Design Lovers", hotel: "A restored machiya", neighborhood: "Central Kyoto", why: "Old bones, modern calm.", price: "Mid–High" },
      { audience: "For Romantics", hotel: "A small inn near the river", neighborhood: "Pontocho", why: "Lantern-lit lanes at your door.", price: "Mid" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Kaiseki once", "Yudofu in winter"] },
      { category: "Local Classics", items: ["Matcha sweets", "Market street food"] },
      { category: "Special Occasion", items: ["A counter sushi omakase"] },
      { category: "Coffee & Cocktails", items: ["A slow pour-over", "A tucked-away sake bar"] },
    ],
    suggestedTripStyles: [
      { title: "Kyoto for Slow Travel", note: "Early mornings, gardens and craft." },
      { title: "Kyoto for Couples", note: "Ryokan nights and lantern-lit lanes." },
      { title: "Kyoto + Tokyo", note: "Old and new Japan in one trip." },
    ],
    relatedSlugs: ["tokyo", "bali", "marrakech"],
    seo: {
      title: "Kyoto Travel Guide — Temples, Craft & Calm | Holideviser",
      description:
        "A Holideviser point of view on Kyoto: when to go, what to experience, what to skip, and how to see old Japan without the crowds.",
    },
  },

  {
    slug: "tokyo",
    name: "Tokyo",
    country: "Japan",
    region: "Asia",
    image: img("1542051841857-5f90071e7989"),
    tagline: "The most exciting city on earth — you just have to pick your lane.",
    shortDescription: "Endless, immaculate and thrilling. The trick is choosing a few neighbourhoods and going deep.",
    tags: ["Food", "Design", "Nightlife"],
    bestFor: ["Foodies", "First-time Asia", "Design lovers"],
    intentions: ["food-obsessed", "design-lovers", "music-nightlife", "first-timers"],
    pov: "Tokyo is too big to 'see' — so don't try. Choose two or three neighbourhoods, eat everything, and let the trains carry you between worlds. The best meals often have six seats and no English menu.",
    whyGo: [
      { title: "Food", note: "Arguably the best eating city in the world." },
      { title: "Neighbourhoods", note: "Each district is a different city." },
      { title: "Design", note: "From concept stores to convenience-store perfection." },
      { title: "Nightlife", note: "Golden Gai to listening bars." },
    ],
    whoFor: [
      "You'll queue an hour for the right bowl of ramen.",
      "You like cities that never quite end.",
      "You want both neon and silence in one day.",
      "You're happy getting productively lost.",
    ],
    whenToGo: {
      bestMonths: "March–April, October–November",
      shoulder: "May, September",
      peak: "Cherry-blossom weeks",
      weather: "Hot, humid summers; crisp, clear autumns.",
      events: ["Cherry blossom (variable)", "Autumn colour"],
    },
    tripLengths: [
      { days: "3 Days", note: "A thrilling first taste." },
      { days: "5 Days", note: "Two or three neighbourhoods, done well." },
      { days: "7 Days", note: "Add a day trip to Hakone or Kamakura." },
    ],
    experiences: {
      do: ["A tiny counter dinner you booked weeks ahead", "A listening bar late"],
      see: ["Shibuya crossing once, then move on", "A quiet garden reset"],
      eat: ["Ramen where locals queue", "Depachika food halls"],
      drink: ["A Golden Gai crawl", "Highballs in a six-seat bar"],
      stay: ["A design hotel or a ryokan-style stay"],
      wander: ["Shimokitazawa's back streets"],
    },
    skip: [
      "Trying to cover all 23 wards — choose and commit.",
      "Chain izakayas near the stations.",
      "The over-hyped themed cafés unless it's your thing.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A minimalist boutique", neighborhood: "Shibuya / Meguro", why: "Walkable to the best food and shops.", price: "High" },
      { audience: "For Night Owls", hotel: "A stylish base near the bars", neighborhood: "Shinjuku", why: "Golden Gai on your doorstep.", price: "Mid–High" },
      { audience: "For Quiet Seekers", hotel: "A calm hotel with a view", neighborhood: "Yanaka", why: "Old-Tokyo lanes and slower mornings.", price: "Mid" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Omakase sushi", "A great bowl of ramen"] },
      { category: "Local Classics", items: ["Tonkatsu", "Convenience-store coffee (really)"] },
      { category: "Special Occasion", items: ["A counter kaiseki or sushi omakase"] },
      { category: "Coffee & Cocktails", items: ["Third-wave pour-over", "A whisky highball bar"] },
    ],
    suggestedTripStyles: [
      { title: "Tokyo for Food Lovers", note: "Reservations first, sightseeing second." },
      { title: "Tokyo After Dark", note: "Listening bars, Golden Gai and late ramen." },
      { title: "Tokyo + Kyoto", note: "Neon and stillness in one trip." },
    ],
    relatedSlugs: ["kyoto", "new-york", "barcelona"],
    seo: {
      title: "Tokyo Travel Guide — Food, Neighbourhoods & Nightlife | Holideviser",
      description:
        "Holideviser's Tokyo guide: where to eat, which neighbourhoods to pick, what to skip and how long to stay in the world's best food city.",
    },
  },

  {
    slug: "bali",
    name: "Bali",
    country: "Indonesia",
    region: "Asia",
    image: img("1555400038-63f5ba517a47"),
    tagline: "Trade the beach clubs for the hills and Bali makes sense again.",
    shortDescription: "Skip the crowds, head inland, and let the rice terraces and slow mornings do the work.",
    tags: ["Wellness", "Nature", "Slow"],
    bestFor: ["Couples", "Wellness seekers", "Honeymooners"],
    intentions: ["slow-escapes", "romantic", "luxury"],
    pov: "Bali's magic isn't the beach club — it's the morning before anyone's awake, mist over the terraces, a slow breakfast, a waterfall that takes effort to reach. Base yourself in the hills and treat the coast as a day trip, not the point.",
    whyGo: [
      { title: "Nature", note: "Rice terraces, jungle and waterfalls." },
      { title: "Wellness", note: "Yoga, spas and genuinely slow days." },
      { title: "Food", note: "Warungs, healthy cafés and long breakfasts." },
      { title: "Stays", note: "Some of Asia's most beautiful villas." },
    ],
    whoFor: [
      "You want to do less, beautifully.",
      "You like a villa and a pool over a resort buffet.",
      "You'll drive an hour for the right waterfall.",
      "You want romance without a resort bubble.",
    ],
    whenToGo: {
      bestMonths: "April–June, September–October",
      shoulder: "March, November",
      peak: "July–August, December",
      weather: "Dry season is best; wet season brings afternoon rain.",
      events: ["Nyepi (day of silence)", "Dry-season festivals"],
    },
    tripLengths: [
      { days: "5 Days", note: "The hills and one stretch of coast." },
      { days: "7 Days", note: "Add islands or a dive trip." },
      { days: "10 Days", note: "Truly unwind; nowhere to be." },
    ],
    experiences: {
      do: ["Sunrise over the terraces", "A proper spa afternoon"],
      see: ["A waterfall off the main road", "Ubud's temples early"],
      eat: ["A warung the locals love", "A long, healthy breakfast"],
      drink: ["Fresh juice on a terrace", "Sunset drinks inland"],
      stay: ["A villa with its own pool"],
      wander: ["Rice-field paths at golden hour"],
    },
    skip: [
      "Kuta's party strip unless that's your whole trip.",
      "The most Instagrammed swing/gate photo queues.",
      "Trying to see the whole island — distances are deceiving.",
    ],
    whereToStay: [
      { audience: "For Romantics", hotel: "A jungle villa", neighborhood: "Ubud", why: "Privacy, pool and terrace views.", price: "Mid–High" },
      { audience: "For Quiet Seekers", hotel: "A wellness retreat", neighborhood: "Sidemen", why: "Fewer crowds, real calm.", price: "Mid" },
      { audience: "For the Big Splurge", hotel: "A clifftop resort", neighborhood: "Uluwatu", why: "Drama, sunsets and service.", price: "High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Babi guling once", "A warung nasi campur"] },
      { category: "Local Classics", items: ["Satay lilit", "Fresh tropical fruit"] },
      { category: "Special Occasion", items: ["A clifftop dinner at sunset"] },
      { category: "Coffee & Cocktails", items: ["Local single-origin coffee", "A terrace sundowner"] },
    ],
    suggestedTripStyles: [
      { title: "Bali for Slow Travel", note: "Hills, spas and unhurried mornings." },
      { title: "Bali for Honeymooners", note: "Villas, sunsets and privacy." },
      { title: "Bali + Islands", note: "Add the Gilis or Nusa for the sea." },
    ],
    relatedSlugs: ["kyoto", "tulum", "sydney"],
    seo: {
      title: "Bali Travel Guide — Slow Escapes & Where to Stay | Holideviser",
      description:
        "Holideviser's Bali guide beyond the beach clubs: the hills, the villas, what to eat and what to skip for a slower, more beautiful trip.",
    },
  },

  {
    slug: "dubai",
    name: "Dubai",
    country: "United Arab Emirates",
    region: "Middle East",
    image: img("1576397189312-199928d647b6"),
    tagline: "Go for the spectacle — stay for the food, the desert and the sun.",
    shortDescription: "Beyond the superlatives: brilliant restaurants, easy beaches and a desert on the doorstep.",
    tags: ["Luxury", "Beach", "Food"],
    bestFor: ["Families", "Couples", "Winter-sun seekers"],
    intentions: ["luxury", "beach-late-nights", "first-timers"],
    pov: "Dubai is easy to dismiss and easy to enjoy. Skip the ranking-chasing and treat it as a winter-sun city: long beach mornings, a genuinely excellent food scene, and a desert that's a short drive from your hotel. Come in the cooler months.",
    whyGo: [
      { title: "Sun", note: "Reliable winter warmth and beaches." },
      { title: "Food", note: "A surprisingly deep, global dining scene." },
      { title: "Desert", note: "Dunes and quiet just outside the city." },
      { title: "Stays", note: "Some of the world's most extravagant hotels." },
    ],
    whoFor: [
      "You want guaranteed winter sun.",
      "You like a beach day and a great dinner.",
      "You'll trade a landmark for a desert night.",
      "You appreciate a hotel that goes all out.",
    ],
    whenToGo: {
      bestMonths: "November–March",
      shoulder: "April, October",
      peak: "December–January",
      weather: "Very hot summers; warm, dry, ideal winters.",
      events: ["Winter dining season", "Desert-season activities"],
    },
    tripLengths: [
      { days: "3 Days", note: "Beach, dinner and one desert night." },
      { days: "5 Days", note: "Add old Dubai and a day trip." },
      { days: "7 Days", note: "Pair with Abu Dhabi or Oman." },
    ],
    experiences: {
      do: ["A desert evening with dinner", "A slow beach morning"],
      see: ["Old Dubai and the creek", "The skyline from the water"],
      eat: ["Emirati food, done properly", "A destination restaurant"],
      drink: ["Sunset drinks over the marina", "Fresh juice in the souk"],
      stay: ["A beach resort or a design hotel"],
      wander: ["Al Fahidi's lanes and galleries"],
    },
    skip: [
      "Summer, unless you love 45°C.",
      "Queuing for the very top observation deck — the view's similar lower down.",
      "Over-scheduling malls; the beaches and desert are the point.",
    ],
    whereToStay: [
      { audience: "For the Big Splurge", hotel: "A landmark resort", neighborhood: "Palm / Jumeirah", why: "The full spectacle, done well.", price: "High" },
      { audience: "For Families", hotel: "A beachfront resort", neighborhood: "JBR", why: "Sand, pools and easy dining.", price: "Mid–High" },
      { audience: "For Design Lovers", hotel: "A boutique city hotel", neighborhood: "DIFC", why: "Galleries and restaurants at the door.", price: "Mid–High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Emirati breakfast", "A desert dinner"] },
      { category: "Local Classics", items: ["Shawarma done right", "Karak chai"] },
      { category: "Special Occasion", items: ["A skyline tasting menu"] },
      { category: "Coffee & Cocktails", items: ["Specialty coffee in DIFC", "A marina sundowner"] },
    ],
    suggestedTripStyles: [
      { title: "Dubai for a Little Luxury", note: "Beach, spa and standout dinners." },
      { title: "Dubai for Families", note: "Sun, sand and easy days." },
      { title: "Dubai + Oman", note: "Add mountains and wadis nearby." },
    ],
    relatedSlugs: ["marrakech", "sydney", "cape-town"],
    seo: {
      title: "Dubai Travel Guide — Winter Sun, Food & Desert | Holideviser",
      description:
        "Holideviser's take on Dubai beyond the superlatives: when to go, where to eat and stay, and what to skip for a genuinely great trip.",
    },
  },

  {
    slug: "cape-town",
    name: "Cape Town",
    country: "South Africa",
    region: "Africa",
    image: img("1588455471455-4b28e9ab3cd5"),
    tagline: "Mountains, wine and ocean in a single day — plan for less, not more.",
    shortDescription: "One of the most beautiful cities anywhere. The mistake is trying to do it all at once.",
    tags: ["Nature", "Food", "Adventure"],
    bestFor: ["Couples", "Adventurers", "Food & wine lovers"],
    intentions: ["adventure", "food-obsessed", "luxury"],
    pov: "Cape Town packs a lifetime into a few square miles — so slow down. Mountain in the morning, coast by lunch, winelands by late afternoon. Watch the weather, keep plans loose, and let the light do the rest.",
    whyGo: [
      { title: "Nature", note: "Table Mountain, beaches and the Cape." },
      { title: "Wine", note: "World-class estates a short drive away." },
      { title: "Food", note: "A confident, creative dining scene." },
      { title: "Value", note: "Exceptional quality for the price." },
    ],
    whoFor: [
      "You want mountain and ocean in one day.",
      "You'll plan an afternoon around a wine estate.",
      "You like a city with genuine wilderness beside it.",
      "You travel for the light as much as the sights.",
    ],
    whenToGo: {
      bestMonths: "November–March",
      shoulder: "October, April",
      peak: "December–January",
      weather: "Warm, dry summers; wetter, cooler winters.",
      events: ["Summer festival season", "Whale-watching (winter/spring)"],
    },
    tripLengths: [
      { days: "5 Days", note: "City, coast and one winelands day." },
      { days: "7 Days", note: "Add the Cape and more wine country." },
      { days: "10 Days", note: "Pair with a safari or the Garden Route." },
    ],
    experiences: {
      do: ["Table Mountain early, before the wind", "A winelands lunch, no rush back"],
      see: ["The Cape peninsula drive", "Sundowners with a view"],
      eat: ["A creative tasting menu", "A seafood lunch by the water"],
      drink: ["Cellar-door tastings", "A rooftop sundowner"],
      stay: ["A design guesthouse or a winelands estate"],
      wander: ["Bo-Kaap's colourful streets"],
    },
    skip: [
      "The cable car on a windy day — check first.",
      "Cramming a safari into a single rushed day.",
      "Ignoring local advice on where and when to walk.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A boutique guesthouse", neighborhood: "De Waterkant", why: "Style, walkability and views.", price: "Mid–High" },
      { audience: "For Romantics", hotel: "A winelands estate", neighborhood: "Constantia", why: "Vineyards and quiet luxury.", price: "High" },
      { audience: "For Quiet Seekers", hotel: "A coastal retreat", neighborhood: "Camps Bay", why: "Sea, sunsets and calm.", price: "Mid–High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["A winelands long lunch", "Fresh line fish"] },
      { category: "Local Classics", items: ["Braai done right", "Cape Malay curry"] },
      { category: "Special Occasion", items: ["A destination tasting menu"] },
      { category: "Coffee & Cocktails", items: ["Excellent local roasters", "A sundowner G&T"] },
    ],
    suggestedTripStyles: [
      { title: "Cape Town for Food & Wine", note: "Estates, tastings and long lunches." },
      { title: "Cape Town for Adventure", note: "Mountain, coast and the Cape." },
      { title: "Cape Town + Safari", note: "Pair the city with the bush." },
    ],
    relatedSlugs: ["marrakech", "sydney", "tulum"],
    seo: {
      title: "Cape Town Travel Guide — Mountain, Wine & Coast | Holideviser",
      description:
        "A Holideviser point of view on Cape Town: when to go, where to eat and stay, and how to balance mountain, ocean and winelands.",
    },
  },

  {
    slug: "marrakech",
    name: "Marrakech",
    country: "Morocco",
    region: "Africa",
    image: img("1773500164240-eac0111621b6"),
    tagline: "Overwhelming on purpose — the trick is a quiet riad to disappear into.",
    shortDescription: "Sensory, chaotic and beautiful. Balance the medina's noise with a courtyard of calm.",
    tags: ["Design", "Culture", "Shopping"],
    bestFor: ["Couples", "Design lovers", "Short breaks"],
    intentions: ["design-lovers", "luxury", "romantic"],
    pov: "Marrakech works best in contrast: lose yourself in the souks for a few hours, then retreat to a riad courtyard as the afternoon peaks. Go with a plan for the medina and no plan for the evening.",
    whyGo: [
      { title: "Design", note: "Riads, tilework and colour everywhere." },
      { title: "Souks", note: "One of the great shopping cities." },
      { title: "Food", note: "Tagines, street food and rooftop dinners." },
      { title: "Escapes", note: "The Atlas and the desert within reach." },
    ],
    whoFor: [
      "You want beauty and chaos in equal measure.",
      "You'll happily haggle for the right rug.",
      "You like a courtyard to escape the afternoon heat.",
      "You want a short, sensory city break.",
    ],
    whenToGo: {
      bestMonths: "March–May, September–November",
      shoulder: "February, December",
      peak: "Spring and autumn weekends",
      weather: "Very hot summers; mild, pleasant spring and autumn.",
      events: ["Spring festival season", "Autumn shoulder calm"],
    },
    tripLengths: [
      { days: "3 Days", note: "Medina, riad life and rooftop dinners." },
      { days: "5 Days", note: "Add the Atlas foothills." },
      { days: "7 Days", note: "Pair with the desert or the coast." },
    ],
    experiences: {
      do: ["The souks with a plan, not a map", "A hammam afternoon"],
      see: ["Jardin Majorelle early", "A riad's rooftop at dusk"],
      eat: ["A slow tagine", "Street food on Jemaa el-Fnaa (carefully chosen)"],
      drink: ["Mint tea, endlessly", "A rooftop sundowner"],
      stay: ["A riad, always"],
      wander: ["The medina's quieter northern lanes"],
    },
    skip: [
      "Following unofficial 'guides' who appear beside you.",
      "The most touristy stalls on the main square.",
      "A rushed one-day desert round-trip.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A restored riad", neighborhood: "Medina", why: "Courtyards, tile and calm behind the noise.", price: "Mid–High" },
      { audience: "For Romantics", hotel: "A boutique with a pool", neighborhood: "Palmeraie", why: "Quiet, green and private.", price: "High" },
      { audience: "For the Big Splurge", hotel: "A landmark hotel", neighborhood: "Hivernage", why: "Old-world glamour.", price: "High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["A proper tagine", "Fresh Moroccan breakfast"] },
      { category: "Local Classics", items: ["Couscous Friday", "Msemen"] },
      { category: "Special Occasion", items: ["A rooftop dinner at sunset"] },
      { category: "Coffee & Cocktails", items: ["Mint tea ritual", "A hotel-bar cocktail"] },
    ],
    suggestedTripStyles: [
      { title: "Marrakech for Design Lovers", note: "Riads, gardens and the souks." },
      { title: "Marrakech for a Long Weekend", note: "Three days, big impact." },
      { title: "Marrakech + Desert", note: "Add dunes and Atlas nights." },
    ],
    relatedSlugs: ["cape-town", "kyoto", "lisbon"],
    seo: {
      title: "Marrakech Travel Guide — Riads, Souks & Design | Holideviser",
      description:
        "Holideviser's opinionated Marrakech guide: where to stay, what to eat, what to skip and how to balance the medina with calm.",
    },
  },

  {
    slug: "new-york",
    name: "New York",
    country: "United States",
    region: "North America",
    image: img("1496588152823-86ff7695e68f"),
    tagline: "Pick a few neighbourhoods, eat constantly, and let the city set the pace.",
    shortDescription: "The whole world in one city. Go deep on a couple of neighbourhoods rather than wide on landmarks.",
    tags: ["Food", "Culture", "Nightlife"],
    bestFor: ["Friends", "Couples", "First-time USA"],
    intentions: ["food-obsessed", "music-nightlife", "design-lovers", "first-timers"],
    pov: "New York rewards specificity. Don't 'do' Manhattan — pick a few neighbourhoods, walk them properly, and eat your way across the boroughs. The best nights start with a plan and end somewhere you didn't expect.",
    whyGo: [
      { title: "Food", note: "Every cuisine, at every price, done well." },
      { title: "Culture", note: "World-class museums, theatre and music." },
      { title: "Neighbourhoods", note: "Each block is a different city." },
      { title: "Energy", note: "There's nowhere quite like it after dark." },
    ],
    whoFor: [
      "You'd rather explore a neighbourhood than a checklist.",
      "You want theatre, museums and a great late dinner.",
      "You like a city that never makes you decide too early.",
      "You'll walk 20,000 steps and love it.",
    ],
    whenToGo: {
      bestMonths: "May–June, September–October",
      shoulder: "April, November",
      peak: "December holidays",
      weather: "Hot, humid summers; cold winters; glorious autumns.",
      events: ["Autumn cultural season", "December holiday displays"],
    },
    tripLengths: [
      { days: "3 Days", note: "A couple of neighbourhoods and one big night." },
      { days: "5 Days", note: "Add the boroughs and a museum day." },
      { days: "7 Days", note: "Live like a local; go slow." },
    ],
    experiences: {
      do: ["A neighbourhood walk end to end", "A show, then a late dinner"],
      see: ["A single great museum, properly", "The skyline from the water"],
      eat: ["A slice done right", "A meal in an outer borough"],
      drink: ["A dive bar and a cocktail bar in one night", "Coffee on a stoop"],
      stay: ["A boutique downtown over a midtown tower"],
      wander: ["The West Village with no destination"],
    },
    skip: [
      "Times Square restaurants — walk through, don't eat there.",
      "Trying to see every landmark in one trip.",
      "Midtown chain hotels if you want a sense of place.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A downtown boutique", neighborhood: "SoHo / Lower East Side", why: "Style and walkability.", price: "High" },
      { audience: "For Night Owls", hotel: "A hotel near the bars", neighborhood: "East Village", why: "Late everything at your door.", price: "Mid–High" },
      { audience: "For First-Timers", hotel: "A classic near the park", neighborhood: "Midtown edge", why: "Central and easy.", price: "Mid–High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["A great slice", "A bagel done properly"] },
      { category: "Local Classics", items: ["Deli sandwich", "Dim sum in Chinatown"] },
      { category: "Special Occasion", items: ["A tasting menu you booked ahead"] },
      { category: "Coffee & Cocktails", items: ["Third-wave coffee", "A proper cocktail bar"] },
    ],
    suggestedTripStyles: [
      { title: "New York for Food Lovers", note: "Boroughs, classics and reservations." },
      { title: "New York After Dark", note: "Shows, bars and late dinners." },
      { title: "New York for First-Timers", note: "The essentials, done smartly." },
    ],
    relatedSlugs: ["tokyo", "mexico-city", "barcelona"],
    seo: {
      title: "New York Travel Guide — Neighbourhoods, Food & Nights | Holideviser",
      description:
        "Holideviser's New York guide: which neighbourhoods to pick, where to eat, what to skip and how long to stay in the city that never stops.",
    },
  },

  {
    slug: "mexico-city",
    name: "Mexico City",
    country: "Mexico",
    region: "Latin America & Caribbean",
    image: img("1645765769720-f4283e2157f4"),
    tagline: "The most exciting food city right now — go hungry, plan dinners first.",
    shortDescription: "Leafy neighbourhoods, world-class food and a culture scene that keeps surprising.",
    tags: ["Food", "Design", "Culture"],
    bestFor: ["Foodies", "Couples", "Design lovers"],
    intentions: ["food-obsessed", "design-lovers", "music-nightlife"],
    pov: "Mexico City is a food city first — book your dinners before your flights. Base yourself in Roma or Condesa, walk the tree-lined streets, and let markets, mezcalerías and museums fill the gaps between meals.",
    whyGo: [
      { title: "Food", note: "From street tacos to the world's best tables." },
      { title: "Neighbourhoods", note: "Roma and Condesa are made for wandering." },
      { title: "Culture", note: "Extraordinary museums and design." },
      { title: "Value", note: "World-class experiences at gentle prices." },
    ],
    whoFor: [
      "You book the restaurant before the flight.",
      "You love a leafy, walkable neighbourhood.",
      "You want culture without the crowds of Europe.",
      "You'll happily eat five times a day.",
    ],
    whenToGo: {
      bestMonths: "March–May",
      shoulder: "October–November",
      peak: "Day of the Dead season",
      weather: "Mild year-round; a rainy season in summer.",
      events: ["Day of the Dead (early November)", "Spring cultural season"],
    },
    tripLengths: [
      { days: "4 Days", note: "Roma–Condesa, markets and museums." },
      { days: "5 Days", note: "Add a day trip to the pyramids." },
      { days: "7 Days", note: "Pair with Oaxaca or the coast." },
    ],
    experiences: {
      do: ["A morning walk through Roma–Condesa", "A mezcal tasting"],
      see: ["A great museum, unhurried", "A market at its liveliest"],
      eat: ["The taco place with no sign", "A destination tasting menu"],
      drink: ["Mezcal, sipped slowly", "Excellent local coffee"],
      stay: ["A design hotel in Roma"],
      wander: ["Condesa's parks and side streets"],
    },
    skip: [
      "Eating only near the Zócalo — the best food is elsewhere.",
      "Driving yourself; traffic will ruin the day.",
      "Rushing the pyramids in the midday heat.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A boutique in Roma", neighborhood: "Roma Norte", why: "Style, cafés and galleries.", price: "Mid" },
      { audience: "For Romantics", hotel: "A quiet Condesa hotel", neighborhood: "Condesa", why: "Leafy, calm and central.", price: "Mid" },
      { audience: "For the Big Splurge", hotel: "A landmark hotel", neighborhood: "Polanco", why: "Fine dining and luxury shopping.", price: "High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Tacos al pastor", "A market breakfast"] },
      { category: "Local Classics", items: ["Chilaquiles", "Tamales"] },
      { category: "Special Occasion", items: ["A world-ranked tasting menu"] },
      { category: "Coffee & Cocktails", items: ["Specialty coffee in Roma", "A mezcalería crawl"] },
    ],
    suggestedTripStyles: [
      { title: "Mexico City for Food Lovers", note: "Markets, tacos and tasting menus." },
      { title: "Mexico City for Design Lovers", note: "Galleries, boutiques and Barragán." },
      { title: "CDMX + Oaxaca", note: "Two of Mexico's great food cities." },
    ],
    relatedSlugs: ["tulum", "lisbon", "new-york"],
    seo: {
      title: "Mexico City Travel Guide — Food, Design & Neighbourhoods | Holideviser",
      description:
        "Holideviser's Mexico City guide: where to eat, which neighbourhoods to base in, what to skip and how long to stay in the world's hottest food city.",
    },
  },

  {
    slug: "tulum",
    name: "Tulum",
    country: "Mexico",
    region: "Latin America & Caribbean",
    image: img("1605216663980-b7ca6e9f2451"),
    tagline: "Beautiful, yes — but the magic is inland, in the cenotes most people drive past.",
    shortDescription: "Beach, ruins and jungle cenotes. Time it right and it still feels like a secret.",
    tags: ["Beach", "Wellness", "Design"],
    bestFor: ["Couples", "Wellness seekers", "Design lovers"],
    intentions: ["beach-late-nights", "romantic", "slow-escapes"],
    pov: "Tulum's beach is the postcard, but the real magic is inland — cenotes at dawn, ruins before the tours, and a design hotel to slow down in. Go in the cooler months and get everywhere early.",
    whyGo: [
      { title: "Beach", note: "That famous Caribbean turquoise." },
      { title: "Cenotes", note: "Jungle swimming holes just off the road." },
      { title: "Design", note: "Some of the most beautiful small hotels anywhere." },
      { title: "Wellness", note: "Yoga, spas and slow mornings." },
    ],
    whoFor: [
      "You want a beach and a jungle in one trip.",
      "You'll wake early for an empty cenote.",
      "You like a design hotel over a mega-resort.",
      "You want beauty with a little edge.",
    ],
    whenToGo: {
      bestMonths: "November–April",
      shoulder: "May, October",
      peak: "December–March",
      weather: "Hot and humid; watch for seaweed and storm season in summer.",
      events: ["Dry-season beach months", "Wellness retreat season"],
    },
    tripLengths: [
      { days: "4 Days", note: "Beach, ruins and a couple of cenotes." },
      { days: "5 Days", note: "Add a day inland or on the water." },
      { days: "7 Days", note: "Pair with a city or island." },
    ],
    experiences: {
      do: ["A cenote before breakfast", "A slow spa afternoon"],
      see: ["The ruins at opening", "Sunrise over the sea"],
      eat: ["Fresh ceviche", "A jungle-side dinner"],
      drink: ["A mezcal cocktail at sunset", "Fresh coconut on the sand"],
      stay: ["A design hotel on the beach road"],
      wander: ["The jungle side, away from the strip"],
    },
    skip: [
      "The beach road at peak time — go early or inland.",
      "Overpriced beach clubs that charge for the view.",
      "Summer, when seaweed can spoil the beaches.",
    ],
    whereToStay: [
      { audience: "For Design Lovers", hotel: "A boutique cabana", neighborhood: "Beach Zone", why: "Barefoot design and jungle-meets-sea style.", price: "High" },
      { audience: "For Romantics", hotel: "An adults-only hideaway", neighborhood: "South Beach", why: "Privacy and quiet luxury.", price: "High" },
      { audience: "For Quiet Seekers", hotel: "A wellness retreat", neighborhood: "Jungle side", why: "Calm, green and unplugged.", price: "Mid–High" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["Fresh ceviche", "Tacos off the strip"] },
      { category: "Local Classics", items: ["Cochinita pibil", "Aguas frescas"] },
      { category: "Special Occasion", items: ["A jungle fine-dining dinner"] },
      { category: "Coffee & Cocktails", items: ["Cold brew in the heat", "A mezcal sundowner"] },
    ],
    suggestedTripStyles: [
      { title: "Tulum for Couples", note: "Design hotels, cenotes and slow days." },
      { title: "Tulum for Wellness", note: "Yoga, spa and beach resets." },
      { title: "Tulum + Mexico City", note: "Beach and the big city in one trip." },
    ],
    relatedSlugs: ["mexico-city", "bali", "cape-town"],
    seo: {
      title: "Tulum Travel Guide — Beach, Cenotes & Design | Holideviser",
      description:
        "Holideviser's Tulum guide beyond the beach clubs: cenotes, ruins, where to stay and what to skip for a slower, more beautiful trip.",
    },
  },

  {
    slug: "sydney",
    name: "Sydney",
    country: "Australia",
    region: "Oceania",
    image: img("1624138784614-87fd1b6528f8"),
    tagline: "Beaches, harbour and brunch — an outdoor city that's easy to love.",
    shortDescription: "Big-nature beauty with a relaxed, coastal-city rhythm and a serious food scene.",
    tags: ["Beach", "Food", "Nature"],
    bestFor: ["First-time Oceania", "Couples", "Families"],
    intentions: ["beach-late-nights", "adventure", "first-timers", "food-obsessed"],
    pov: "Sydney is an outdoor city, so plan around the light and the water. Swim before breakfast, walk a coastal path, eat well and let the harbour anchor the trip. Save the big-ticket sights for one clear morning.",
    whyGo: [
      { title: "Beaches", note: "Ocean pools and coastal walks in the city." },
      { title: "Harbour", note: "One of the world's great settings." },
      { title: "Food", note: "Brilliant produce and a confident dining scene." },
      { title: "Nature", note: "Blue Mountains and beaches within reach." },
    ],
    whoFor: [
      "You want a swim before breakfast.",
      "You like a city that lives outdoors.",
      "You'll walk a coastal path for the views.",
      "You want easy beauty on a first big trip.",
    ],
    whenToGo: {
      bestMonths: "October–April",
      shoulder: "September, May",
      peak: "December–January (summer)",
      weather: "Warm summers; mild winters; a true Southern-Hemisphere season.",
      events: ["Summer festival season", "Coastal-walk weather"],
    },
    tripLengths: [
      { days: "4 Days", note: "Harbour, beaches and a coastal walk." },
      { days: "5 Days", note: "Add the Blue Mountains." },
      { days: "7 Days", note: "Pair with wine country or the coast." },
    ],
    experiences: {
      do: ["The Bondi-to-Coogee coastal walk", "A morning ocean-pool swim"],
      see: ["The harbour from the water", "A beach sunrise"],
      eat: ["A long weekend brunch", "Fresh seafood by the water"],
      drink: ["Small-bar hopping in the city", "A beachside sundowner"],
      stay: ["A boutique near the harbour or the beach"],
      wander: ["Paddington's terraces and boutiques"],
    },
    skip: [
      "Doing only the Opera House and leaving.",
      "Driving in the CBD — walk and ferry instead.",
      "Bondi mid-summer midday if you dislike crowds.",
    ],
    whereToStay: [
      { audience: "For First-Timers", hotel: "A harbour-side hotel", neighborhood: "The Rocks", why: "Walkable to the icons and ferries.", price: "Mid–High" },
      { audience: "For Beach Lovers", hotel: "A boutique near the sand", neighborhood: "Bondi", why: "Swim before breakfast.", price: "Mid–High" },
      { audience: "For Design Lovers", hotel: "A stylish inner-city stay", neighborhood: "Surry Hills", why: "Best cafés and small bars.", price: "Mid" },
    ],
    foodDrink: [
      { category: "Don't Miss", items: ["A great flat white", "Fresh seafood"] },
      { category: "Local Classics", items: ["Weekend brunch", "A proper meat pie"] },
      { category: "Special Occasion", items: ["A harbour-view tasting menu"] },
      { category: "Coffee & Cocktails", items: ["World-class coffee", "Surry Hills small bars"] },
    ],
    suggestedTripStyles: [
      { title: "Sydney for First-Timers", note: "Harbour, beaches and coastal walks." },
      { title: "Sydney for Beach Days", note: "Ocean pools and long, easy mornings." },
      { title: "Sydney + Blue Mountains", note: "City and big nature in one trip." },
    ],
    relatedSlugs: ["cape-town", "bali", "dubai"],
    seo: {
      title: "Sydney Travel Guide — Beaches, Harbour & Food | Holideviser",
      description:
        "Holideviser's Sydney guide: beaches, coastal walks, where to eat and stay, and what to skip in Australia's easy-to-love harbour city.",
    },
  },
];

// Demo catalogue destinations are unpublished (hidden from public detail routes in 10A/10A.1).
// Records are retained internally; only entries explicitly marked { published: true } render as guides.
export const getBySlug = (slug) =>
  DESTINATIONS.find((d) => d.slug === slug && d.published === true);
